/**
 * The two dialogs behind the failed-payments row: the reminder mail, and stopping the
 * retries.
 *
 * **Email them and Chase are the same call.** `payment_failed_reminder`'s `next_step`
 * is "what happens next, in one sentence" (its own `.describe()`), and the difference
 * between the mockup's two buttons is entirely that sentence: one says a retry is
 * coming, the other says nobody is going to try again. A second template for the
 * second wording would be two templates whose only difference is a paragraph
 * somebody has to keep in step.
 *
 * `notifications_send` is on `actionFields.ts`'s `HAND_WRITTEN` list — `message` is a
 * discriminated union over every template — so `ActionForm` will not draw it and this
 * builds the body. Every param is already on the row: the reference, the amount, the
 * attempt count and the days past due.
 */

import { useState } from 'react'

import type { Money, StudioMoneyView } from '@fareast/shared'

import { messageOf } from '../../../api/problem.js'
import { reviewOf, useStudioAction } from '../../../api/studio.js'
import { NeedsReviewNotice } from '../../../components/studio/NeedsReviewNotice.js'
import { useZodForm, Textarea } from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { ConfirmDialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { Prose, Ref } from '../../../components/portal/Text.js'
import { useFormat } from '../../../format/useFormat.js'
import type { PastDue } from './MoneyPage.js'
import { z } from 'zod'
import { absoluteUrl } from '../../../integrations/navigate.js'

type FailedRow = StudioMoneyView['failed_payments'][number]

/**
 * Where the mail sends the reader.
 *
 * `modules/billing/charge.ts` builds the same URL as `${baseUrl}/app/billing/${ref}`
 * for every automatic payment mail, and this is that path on the origin the browser is
 * on. The two-line idea under it — an absolute URL on this origin, for a link somebody
 * outside this tab will click — is `integrations/navigate.ts`'s now, shared with
 * `routes/login.tsx`'s magic-link callback.
 */
export function invoiceUrl(ref: string): string {
  return absoluteUrl(`/app/billing/${ref}`)
}

/**
 * The sentence the mail closes on.
 *
 * Written from the row rather than typed by the sender, because it is a statement of
 * fact about the retry schedule and `billing/retrySchedule.ts` is what decides it.
 */
export function nextStepOf(row: FailedRow, retryDay: string | null, exhausted: boolean): string {
  if (retryDay !== null) {
    return `We will try the ${railWord(row.rail)} again on ${retryDay}. Nothing needs to happen before then unless the details on file have changed.`
  }
  if (exhausted) {
    return `We are not going to try again automatically — ${String(row.of)} ${row.of === 1 ? 'attempt has' : 'attempts have'} been made. Paying it in the portal, or telling us what to change, is what closes it.`
  }
  /*
   * Attempts left and nothing scheduled. Saying "we will not try again" here would be
   * a promise the studio can break with one click of `invoices_retry_charge$`, so it
   * says what is actually true: nothing is booked in.
   */
  return `Nothing further is scheduled at the moment — ${String(row.attempt)} of ${String(row.of)} ${row.of === 1 ? 'attempt' : 'attempts'} has been used. Paying it in the portal, or telling us what to change, is what closes it.`
}

/**
 * What is still owed on a failed row's invoice — the figure both dialogs state.
 *
 * `analytics_receivables`' `outstanding` (`MoneyPage.tsx` `pastDueOf`), which is total
 * less what was paid **and** what was written off, worked out on the server.
 * `FailedPaymentSchema` carries only the invoice's `total`, and that is the fallback
 * for a row receivables has nothing for — one that is not past due. One function
 * rather than the reminder's own line, because Cancel retry states the same figure:
 * after session 44 a failed invoice can have part of it written off, and "Vela still
 * owes" the face value of an invoice the studio has forgiven part of is the wrong
 * sentence.
 */
export function owedOn(row: FailedRow, pastDue: PastDue | undefined): Money {
  return pastDue?.outstanding ?? row.total
}

function railWord(rail: FailedRow['rail']): string {
  if (rail === 'card') return 'card'
  if (rail === 'ach' || rail === 'sepa') return 'debit'
  return 'payment'
}

/* --------------------------------------------------------------- the reminder */

const NoteFormSchema = z.object({ next_step: z.string().trim().min(10).max(600) })
type NoteForm = z.infer<typeof NoteFormSchema>

export function ReminderDialog({
  row,
  pastDue,
  exhausted,
  clientName,
  onClose,
}: {
  readonly row: FailedRow
  /** What is still owed and for how long. Undefined for a row nothing is owed on. */
  readonly pastDue: PastDue | undefined
  /** `FailedPaymentSchema.exhausted` — the read model's own, not a re-derivation. */
  readonly exhausted: boolean
  readonly clientName: string
  readonly onClose: () => void
}) {
  const format = useFormat()
  const send = useStudioAction('notifications_send')
  const review = reviewOf(send.data)

  /*
   * **The outstanding amount, not the invoice total.**
   *
   * `payment_failed_reminder.amount` is described as "what is outstanding" and the
   * mail puts it in its hero figure. `FailedPaymentSchema.total` is the invoice's
   * total, which is a different number the moment part of it has settled — an
   * on-account credit, a partial transfer — and chasing somebody for more than they
   * owe is the worst thing this button can do. The total is the fallback for a row
   * receivables has nothing for, which is a row that is not past due at all.
   */
  const owed = owedOn(row, pastDue)
  const days = pastDue?.days ?? 0

  /*
   * **UTC, because the table beside it is UTC.** The failed-payments table renders
   * `next_retry_at` through `<DateText zone="UTC">`, and this sentence goes into a
   * mail naming the same instant. Left to the viewer's own zone, a studio member in
   * Tokyo reading a 21:30 UTC retry would send a client a mail saying tomorrow
   * about the day the screen in front of them calls today.
   */
  const retryDay =
    row.next_retry_at === null ? null : format.date(row.next_retry_at, 'short', 'UTC')
  const form = useZodForm<NoteForm, NoteForm>(NoteFormSchema, {
    defaultValues: { next_step: nextStepOf(row, retryDay, exhausted) },
  })
  const nextStep = form.watch('next_step')

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={exhausted ? 'Chase an exhausted charge' : 'Remind them about a failed charge'}
      confirmLabel={`Email ${clientName} about ${format.money(owed)} on ${row.invoice_ref}`}
      busy={send.isPending || nextStep.trim().length < 10}
      onConfirm={() => {
        if (nextStep.trim().length < 10) return
        send.mutate(
          {
            client: row.client,
            message: {
              template: 'payment_failed_reminder',
              params: {
                invoice_ref: row.invoice_ref,
                amount: owed,
                days_past_due: days,
                attempts: row.attempt,
                next_step: nextStep.trim(),
                url: invoiceUrl(row.invoice_ref),
              },
            },
          },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`It goes to whoever on ${clientName} is set to hear about payments — that switch cannot be turned off, so somebody will get it. It states `}
          <Ref value={row.invoice_ref} />
          {`, ${format.money(owed)} still outstanding, ${String(days)} ${days === 1 ? 'day' : 'days'} past due, and ${String(row.attempt)} failed ${row.attempt === 1 ? 'attempt' : 'attempts'}.`}
        </Prose>
        <Textarea
          control={form.control}
          name="next_step"
          label="What happens next"
          hint="One sentence. It is the only part of this mail that is not read off the row."
        />
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {send.isError ? (
          <Notice need role="alert">
            {messageOf(send.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}

/* ------------------------------------------------------------- cancel a retry */

export function CancelRetryDialog({
  row,
  pastDue,
  clientName,
  onClose,
}: {
  readonly row: FailedRow
  /** What is still owed and for how long — the reminder's own, see `owedOn`. */
  readonly pastDue: PastDue | undefined
  readonly clientName: string
  readonly onClose: () => void
}) {
  const format = useFormat()
  const cancel = useStudioAction('invoices_cancel_retry')
  const review = reviewOf(cancel.data)
  const [tried, setTried] = useState(false)
  /* What the next retry would have charged, and what they still owe without one. */
  const owed = owedOn(row, pastDue)

  return (
    <ConfirmDialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title="Stop retrying a failed charge"
      confirmLabel={`Stop retrying ${format.money(owed)} against ${row.invoice_ref}`}
      busy={cancel.isPending}
      onConfirm={() => {
        setTried(true)
        cancel.mutate(
          { invoice_ref: row.invoice_ref },
          {
            onSuccess: (result) => {
              if (reviewOf(result) === null) onClose()
            },
          },
        )
      }}
    >
      <Stack>
        <Prose>
          {`No further attempt will be made on ${clientName}’s ${format.money(owed)}. The invoice stays open and stays past due; this only stops the schedule, and ${clientName} still owes it.`}
        </Prose>
        {review === null ? null : <NeedsReviewNotice review={review} />}
        {tried && cancel.isError ? (
          <Notice need role="alert">
            {messageOf(cancel.error)}
          </Notice>
        ) : null}
      </Stack>
    </ConfirmDialog>
  )
}
